import { FC, useEffect, useState } from 'react'
import { Transition, TransitionStatus } from 'react-transition-group'
import { IconButton } from '@material-ui/core'
import Close from '@material-ui/icons/Close'
import { wikiSideBarStyle } from './WikiSideBar.style'
import { maxSidebarWidth, sidebarAnimationDuration, rightSidebarWidth as sidebarWidth, wikiSideBarBaseZIndex } from '../../../Constants'
import { useDispatch, useSelector } from 'react-redux'
import WikiBrowserComponent from '../../wiki/wikibrowser/WikiBrowser'
import WikiDocumentComponent from '../../wiki/wikidocument/WikiDocument'
import { wikiActions } from 'src/actions/WikiActions'
import { WikiView } from 'src/reducers/WikiReducer'
import { RootState } from 'src/Store'
import { InfoView } from 'src/reducers/InfoMenuReducer'
import { Rnd } from 'react-rnd'
import { generalActions } from 'src/actions/GeneralActions'
import { getBarStyle, getBarTransitionStyle } from 'src/utils/Utils'
import WikiCategoryComponent from '../wikicategory/WikiCategory'

export interface WikiSideBarProps {
  viewType: WikiView
  navigationIndex: number
}

const defaultProps: WikiSideBarProps = {
  viewType: WikiView.WikiBrowser,
  navigationIndex: 0
}

/**
 * WikiSideBar React component.
 * @param {object} props React properties object.
 * @returns React view component.
 */
export const WikiSideBar: FC<WikiSideBarProps> = (
  props: WikiSideBarProps
) => {
  const dispatch = useDispatch()
  const classes = wikiSideBarStyle()

  const [animateIn, setAnimateIn] = useState<boolean>(false)
  const [showSearchIcon, setShowSearchIcon] = useState<boolean>(false)
  const [showCloseIcon, setShowCloseIcon] = useState<boolean>(false)

  const showWikiSideBar = useSelector((state: RootState): boolean => state.wikiState.showWikiSideBar)
  const currentInfoMenuView = useSelector((state: RootState): InfoView => state.infoMenuState.currentView)
  const dragging = useSelector((state: RootState): Boolean => state.generalState.rightSideBarDragging)
  const rightSideBarWidth = useSelector((state: RootState): number => state.generalState.rightSideBarWidth)

  const enableBackNavigation = props.navigationIndex > 0 || currentInfoMenuView !== InfoView.Empty

  const sidebarStyle = getBarStyle(dragging === true ? 0 : sidebarAnimationDuration)
  const sidebarTransitionStyles = getBarTransitionStyle(rightSideBarWidth)

  /**
   * Set to empty view when close button is clicked.
   */
  const closeSideBarClicked = (): void => {
    dispatch(wikiActions.clearViews())
  }

  useEffect(() => {
    setAnimateIn(true)
    setShowCloseIcon(true)
    setTimeout(function () {
      setShowSearchIcon(true)
    }, sidebarAnimationDuration)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    setAnimateIn(showWikiSideBar)
    if (showWikiSideBar) {
      setShowCloseIcon(true)
      setTimeout(function () {
        setShowSearchIcon(true)
      }, sidebarAnimationDuration)
    } else {
      setShowCloseIcon(false)
      setShowSearchIcon(false)
    }
    // eslint-disable-next-line
  }, [showWikiSideBar])

  return (
    <div style={{ zIndex: wikiSideBarBaseZIndex + props.navigationIndex }}>
      <Transition in={animateIn} out={!animateIn} timeout={0}>
        {(state: TransitionStatus) => (
          <div className={classes.wrapper}>
            <div
              className={`${classes.root}`}
              style={{ ...sidebarStyle, ...sidebarTransitionStyles[state] }}
            >
              <Rnd
                disableDragging
                enableResizing={{ left: true }}
                position={{ x: 0, y: 0 }}
                size={{ width: rightSideBarWidth, height: 'auto' }}
                minWidth={sidebarWidth}
                maxWidth={maxSidebarWidth}
                onResizeStart={() => {
                  dispatch(generalActions.setRightSideBarDragging(true))
                }}
                onResize={(e, direction, ref, delta, position) => {
                  dispatch(generalActions.setRightSideBarWidth(parseInt(ref.style.width)))
                }}
                onResizeStop={(e, direction, ref, delta, position) => {
                  dispatch(generalActions.setRightSideBarWidth(parseInt(ref.style.width)))
                  dispatch(generalActions.setRightSideBarDragging(false))
                }}
              >
                {dragging === false && (
                  <div className={classes.root}>
                    {props.viewType === WikiView.WikiBrowser && (
                      <WikiBrowserComponent
                        showContent={animateIn}
                        enableSearch={showSearchIcon}
                        enableBackNavigation={enableBackNavigation}
                        onBackPress={() => {
                          setAnimateIn(false)
                          setShowSearchIcon(false)
                          setTimeout(function () {
                            dispatch(wikiActions.clearViews())
                          }, sidebarAnimationDuration)
                        }}
                      />
                    )}
                    {props.viewType === WikiView.WikiDocument && (
                      <WikiDocumentComponent
                        enableBackNavigation={enableBackNavigation}
                        onBackPress={() => {
                          dispatch(wikiActions.clearDocument())
                          setAnimateIn(false)
                          setTimeout(function () {
                            dispatch(wikiActions.popView())
                          }, sidebarAnimationDuration)
                        }}
                      />
                    )}
                    {props.viewType === WikiView.WikiCategory && (
                      <WikiCategoryComponent
                        enableBackNavigation={enableBackNavigation}
                        onBackPress={() => {
                          setAnimateIn(false)
                          setTimeout(function () {
                            dispatch(wikiActions.popView())
                          }, sidebarAnimationDuration)
                        }}
                      />
                    )}
                    {showCloseIcon && (
                      <div className={classes.closeButton}>
                        <IconButton size='medium' className={classes.closeButtonIcon} onClick={closeSideBarClicked}>
                          <Close />
                        </IconButton>
                      </div>
                    )}
                  </div>
                )}
              </Rnd>
            </div>
          </div>
        )}
      </Transition>
    </div>
  )
}

WikiSideBar.defaultProps = defaultProps

export default WikiSideBar
